import BroadcastCaptureActivityChannel, {
	type CaptureActivityMessage,
} from '@Infra/CaptureActivity/BroadcastCaptureActivityChannel';

export type ShareCaptureActivityOptions = {
	readonly onElsewhereActivityChange: (isActiveElsewhere: boolean) => void;
	readonly channel?: BroadcastCaptureActivityChannel;
	readonly now?: () => number;
	readonly heartbeatMs?: number;
	readonly expiryMs?: number;
	readonly tabId?: string;
};

export type CaptureActivitySharing = {
	announce: (isActiveHere: boolean) => void;
	dispose: () => void;
};

const DEFAULT_HEARTBEAT_MS = 5000;
const DEFAULT_EXPIRY_MS = 15000;

const isCaptureActivityMessage = (data: unknown): data is CaptureActivityMessage => {
	if (typeof data !== 'object' || data === null || !('type' in data)) {
		return false;
	}

	if (data.type === 'capture-activity-probe') {
		return true;
	}

	return (
		(data.type === 'capture-active' || data.type === 'capture-inactive') &&
		'tabId' in data &&
		typeof data.tabId === 'string'
	);
};

/**
 * Shares the "capture in progress" state between tabs. Peers are tracked by tab id with their last
 * sign of life: an active tab repeats `capture-active` every heartbeat, and a peer silent for
 * longer than the expiry is forgotten, which covers a tab killed without a closing message.
 * `onElsewhereActivityChange` only fires when the aggregated flag actually flips.
 */
const shareCaptureActivity = (options: ShareCaptureActivityOptions): CaptureActivitySharing => {
	const channel = options.channel ?? new BroadcastCaptureActivityChannel();
	const now = options.now ?? Date.now;
	const heartbeatMs = options.heartbeatMs ?? DEFAULT_HEARTBEAT_MS;
	const expiryMs = options.expiryMs ?? DEFAULT_EXPIRY_MS;
	const tabId = options.tabId ?? crypto.randomUUID();

	const peers = new Map<string, number>();
	let isActiveHere = false;
	let isActiveElsewhere = false;
	let heartbeatTimer: ReturnType<typeof setInterval> | undefined;

	const refreshElsewhereActivity = (): void => {
		const next = peers.size > 0;

		if (next === isActiveElsewhere) {
			return;
		}

		isActiveElsewhere = next;
		options.onElsewhereActivityChange(next);
	};

	const postActive = (): void => {
		channel.post({ type: 'capture-active', tabId });
	};

	const handleMessage = (data: unknown): void => {
		if (!isCaptureActivityMessage(data)) {
			return;
		}

		if (data.type === 'capture-activity-probe') {
			if (isActiveHere) {
				postActive();
			}
			return;
		}

		if (data.tabId === tabId) {
			return;
		}

		if (data.type === 'capture-active') {
			peers.set(data.tabId, now());
		} else {
			peers.delete(data.tabId);
		}

		refreshElsewhereActivity();
	};

	const sweepExpiredPeers = (): void => {
		const currentTime = now();

		for (const [peerId, lastSeenAt] of peers) {
			if (currentTime - lastSeenAt > expiryMs) {
				peers.delete(peerId);
			}
		}

		refreshElsewhereActivity();
	};

	const sweepTimer = setInterval(sweepExpiredPeers, heartbeatMs);

	const stopHeartbeat = (): void => {
		clearInterval(heartbeatTimer);
		heartbeatTimer = undefined;
	};

	channel.open(handleMessage);
	channel.post({ type: 'capture-activity-probe' });

	const announce = (isActive: boolean): void => {
		if (isActive === isActiveHere) {
			return;
		}

		isActiveHere = isActive;

		if (isActive) {
			postActive();
			heartbeatTimer = setInterval(postActive, heartbeatMs);
			return;
		}

		stopHeartbeat();
		channel.post({ type: 'capture-inactive', tabId });
	};

	const dispose = (): void => {
		if (isActiveHere) {
			channel.post({ type: 'capture-inactive', tabId });
			isActiveHere = false;
		}

		stopHeartbeat();
		clearInterval(sweepTimer);
		channel.close();
	};

	return { announce, dispose };
};

export default shareCaptureActivity;
